
import { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { 
  SimulationStep,
  SimulationLog,
  SimulationResult,
  Entity
} from './types';
import {
  generateSimulationResult,
  getStepDuration
} from './simulationUtils';

export const useSimulation = (
  policy: { id?: number; name?: string },
  entities: Entity[],
  simulationSteps: SimulationStep[]
) => {
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [currentStep, setCurrentStep] = useState(0);
  const [executionLogs, setExecutionLogs] = useState<SimulationLog[]>([]);
  const [selectedEntities, setSelectedEntities] = useState<string[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [simulationCompleted, setSimulationCompleted] = useState(false);
  const [simulationResults, setSimulationResults] = useState<SimulationResult | null>(null);
  const { toast } = useToast();

  const addLog = (log: SimulationLog) => {
    setExecutionLogs(prev => [...prev, log]);
  };

  const toggleEntitySelection = (entityId: string) => {
    setSelectedEntities(prev => 
      prev.includes(entityId) 
        ? prev.filter(id => id !== entityId)
        : [...prev, entityId]
    );
  };

  const startSimulation = () => {
    if (selectedEntities.length === 0) {
      toast({
        title: "No entities selected",
        description: "Please select at least one entity to process",
        variant: "destructive"
      });
      return;
    }
    
    setExecutionLogs([]);
    setProgress(0);
    setCurrentStep(0);
    setSimulationCompleted(false);
    setShowResults(false);
    setSimulationResults(null);
    setIsRunning(true);
    
    toast({
      title: "Simulation Started",
      description: `Running ${policy?.name} on ${selectedEntities.length} entities`,
    });
    
    addLog({
      type: 'info',
      message: `Initializing policy executor for "${policy?.name}"`,
      timestamp: new Date(),
    });
  };
  
  const pauseSimulation = () => {
    setIsRunning(false);
    
    addLog({
      type: 'warning',
      message: "Simulation paused by user",
      timestamp: new Date(),
    });
    
    toast({
      title: "Simulation Paused",
      description: "You can resume at any time",
    });
  };
  
  const resetSimulation = () => {
    setIsRunning(false);
    setProgress(0);
    setCurrentStep(0);
    setExecutionLogs([]);
    setSimulationCompleted(false);
    setShowResults(false);
    setSimulationResults(null);
    
    toast({
      title: "Simulation Reset",
      description: "All progress has been cleared",
    });
  };
  
  const clearLogs = () => {
    setExecutionLogs([]);
  };

  useEffect(() => {
    if (!isRunning) return;
    
    const stepDuration = getStepDuration(speed);
    
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setIsRunning(false);
          setSimulationCompleted(true);
          
          // Generate final report
          const results = generateSimulationResult(policy?.name || '', selectedEntities);
          setSimulationResults(results);
          setShowResults(true);
          
          toast({
            title: "Simulation Completed",
            description: "View the results to see what actions were taken",
          });
          
          addLog({
            type: 'success',
            message: "Policy execution completed successfully",
            timestamp: new Date(),
          });
          
          addLog({
            type: 'info',
            message: `Generated report: ${results.actionsPerformed} actions performed, compliance improved by ${results.complianceImprovement}`,
            timestamp: new Date(),
          });
          
          return 100;
        }
        
        const newProgress = prev + (100 / (simulationSteps.length * 10)) * speed;
        const completedSteps = Math.floor((newProgress / 100) * simulationSteps.length);
        
        if (completedSteps > currentStep && completedSteps < simulationSteps.length) {
          setCurrentStep(completedSteps);
          const step = simulationSteps[completedSteps];
          
          addLog({
            type: 'info',
            message: `Starting step: ${step.name} - ${step.description}`,
            timestamp: new Date(),
          });
          
          if (step.id === 3) {
            selectedEntities.forEach(entityId => {
              const entity = entities.find(e => e.id === entityId);
              if (entity) {
                addLog({
                  type: entity.status === 'inactive' ? 'warning' : 'info',
                  message: `Analyzing ${entity.type} "${entity.name}"`,
                  entityId,
                  timestamp: new Date(),
                });
              }
            });
          }
          
          if (step.id === 5) {
            selectedEntities.forEach(entityId => {
              const entity = entities.find(e => e.id === entityId);
              if (entity) {
                const actions = [];
                
                if (entity.status === 'inactive') {
                  actions.push('sent notification to owner');
                  actions.push(entity.type === 'team' ? 'flagged for archival' : 'suggested renaming');
                } else if (entity.status === 'empty') {
                  actions.push('suggested adding content');
                }
                
                if (actions.length > 0) {
                  addLog({
                    type: 'success',
                    message: `Actions for ${entity.type} "${entity.name}": ${actions.join(', ')}`,
                    entityId,
                    timestamp: new Date(),
                  });
                } else {
                  addLog({
                    type: 'info',
                    message: `No actions needed for ${entity.type} "${entity.name}"`,
                    entityId,
                    timestamp: new Date(),
                  });
                }
              }
            });
          }
          
          if (step.id === 6) {
            addLog({
              type: 'info',
              message: 'Compiling execution report...',
              timestamp: new Date(),
            });
          }
        }
        
        return newProgress;
      });
    }, stepDuration / 10);
    
    return () => clearInterval(interval);
  }, [isRunning, speed, currentStep, selectedEntities, entities, simulationSteps, policy?.name, toast]);

  return {
    isRunning,
    progress,
    speed,
    currentStep,
    executionLogs,
    selectedEntities,
    showResults,
    simulationCompleted,
    simulationResults,
    setSpeed,
    toggleEntitySelection,
    startSimulation,
    pauseSimulation,
    resetSimulation,
    clearLogs,
    setShowResults
  };
};
